import { type CSSProperties, useEffect, useState } from 'react'
import {
  ROUTE_NAMES,
  type UsageLoad,
  type UsageSummary,
  allowance,
  fillDays,
  loadUsage,
} from '../lib/usage'

// "Usage": how the customer's FreightRates allowance is spent, this month: calls and credits, progress to the
// cap, a day-by-day curve (MCP calls marked), the split by tool and by key. Mounted inside LanesBoard so it
// shares the board's --wr-* tokens. It hides itself when there is no session to ask with (the board is also
// mounted inside the Navo24 workspace, which has no site session) and says so quietly when the API is down.

const mono = "var(--font-mono, ui-monospace, 'SF Mono', Menlo, monospace)"
const label: CSSProperties = {
  fontFamily: mono,
  fontSize: 10,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: 'var(--wr-muted)',
}
const card: CSSProperties = {
  background: 'var(--wr-card)',
  border: '1px solid var(--wr-line)',
  borderRadius: 14,
  boxShadow: 'var(--wr-shadow)',
  padding: 20,
  marginTop: 16,
}
const th: CSSProperties = {
  ...label,
  fontWeight: 600,
  textAlign: 'left',
  padding: '8px 10px',
  borderBottom: '1px solid var(--wr-line)',
}
const td: CSSProperties = {
  padding: '9px 10px',
  borderBottom: '1px solid var(--wr-line)',
  fontSize: 14,
}
const num: CSSProperties = { ...td, fontFamily: mono, textAlign: 'right' }

const n = (v: number) => v.toLocaleString('en-US')
const dayLabel = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { day: 'numeric', month: 'short', timeZone: 'UTC' })
const fullDay = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
const windowTitle = (u: UsageSummary) =>
  u.anchored
    ? `${fullDay(u.period_start)} to ${fullDay(u.period_end)}`
    : new Date(`${u.month}-01T00:00:00Z`).toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      })

export function UsagePanel() {
  const [offset, setOffset] = useState(0)
  const [load, setLoad] = useState<UsageLoad | null>(null)

  useEffect(() => {
    let live = true
    setLoad(null)
    loadUsage(offset).then((r) => live && setLoad(r))
    return () => {
      live = false
    }
  }, [offset])

  if (load && (load.state === 'signed_out' || load.state === 'no_org')) return null

  const usage = load?.state === 'ok' ? load.usage : null
  return (
    <section style={card} aria-label="Usage">
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <div style={label}>Usage</div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>
            {usage ? windowTitle(usage) : 'Loading'}
          </div>
          {usage && (
            <div style={{ ...muted, marginTop: 2 }}>
              {usage.plan_label} plan
              {usage.anchored &&
                offset === 0 &&
                (usage.expired
                  ? ` · ended ${fullDay(usage.period_end)}`
                  : ` · valid until ${fullDay(usage.period_end)}`)}
              {usage.anchored && offset > 0 && ' · earlier period'}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            style={nav}
            disabled={offset >= 24}
            onClick={() => setOffset(offset + 1)}
            aria-label="Earlier period"
          >
            ‹
          </button>
          <button
            type="button"
            style={nav}
            disabled={offset === 0}
            onClick={() => setOffset(offset - 1)}
            aria-label="Later period"
          >
            ›
          </button>
        </div>
      </div>

      {!load && <p style={{ ...muted, margin: '14px 0 0' }}>Loading your usage…</p>}
      {load?.state === 'error' && (
        <p style={{ ...muted, margin: '14px 0 0' }}>
          Usage is not available right now. Try again in a minute.
        </p>
      )}
      {load?.state === 'ok' && <Body u={load.usage} />}
    </section>
  )
}

const muted: CSSProperties = { fontSize: 13, color: 'var(--wr-ink2)', lineHeight: 1.5 }
const nav: CSSProperties = {
  font: 'inherit',
  fontSize: 16,
  width: 34,
  height: 34,
  borderRadius: 10,
  cursor: 'pointer',
  border: '1px solid var(--wr-line)',
  background: 'var(--wr-card)',
  color: 'var(--wr-ink)',
}

function Body({ u }: { u: UsageSummary }) {
  const allow = allowance(u.keys)
  const cap = allow?.cap ?? null
  const pct = cap ? Math.min(100, Math.round((u.calls / cap) * 100)) : null
  const days = fillDays(u.period_start, u.period_end, u.by_day)
  const peak = Math.max(1, ...days.map((d) => d.calls))
  const mcp = u.by_source.find((s) => s.source === 'mcp')?.calls ?? 0
  const logStartsLate = !u.anchored && u.logged_since != null && u.logged_calls < u.calls
  const bar = pct != null && pct >= 100 ? 'var(--wr-bad)' : 'var(--wr-signal)'

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 28, marginTop: 16 }}>
        <Stat
          k="Calls this month"
          v={n(u.calls)}
          sub={cap ? `of ${n(cap)} included` : 'no monthly limit set'}
        />
        <Stat k="Credits used" v={n(u.credits)} sub="1 credit per call" />
        <Stat
          k="Through MCP"
          v={n(mcp)}
          sub={
            u.logged_calls
              ? `${Math.round((mcp / u.logged_calls) * 100)}% of logged calls`
              : 'none logged yet'
          }
        />
        <Stat
          k={u.anchored ? 'Valid until' : 'Resets'}
          v={dayLabel(u.period_end)}
          sub={
            u.anchored
              ? u.expired
                ? 'not renewed'
                : 'one month from purchase'
              : 'UTC, start of next month'
          }
        />
      </div>

      {pct != null && (
        <div style={{ marginTop: 14 }}>
          <div
            role="img"
            aria-label={`${pct}% of the monthly allowance used`}
            style={{ height: 8, borderRadius: 99, background: 'var(--wr-sub)', overflow: 'hidden' }}
          >
            <div style={{ width: `${pct}%`, height: '100%', background: bar }} />
          </div>
          <div style={{ ...muted, marginTop: 6 }}>
            {pct}% used
            {u.refused_calls > 0 && ` · ${n(u.refused_calls)} refused over the limit`}
          </div>
        </div>
      )}

      <div style={{ ...label, marginTop: 20 }}>Calls per day</div>
      <div
        role="img"
        aria-label={`Calls per day, ${windowTitle(u)}, ${n(u.logged_calls)} logged`}
        style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 84, marginTop: 8 }}
      >
        {days.map((d) => (
          <div
            key={d.day}
            title={`${dayLabel(d.day)}: ${n(d.calls)} calls${d.mcp ? `, ${n(d.mcp)} via MCP` : ''}${d.errors ? `, ${n(d.errors)} errors` : ''}`}
            style={{
              flex: 1,
              minWidth: 2,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              height: '100%',
            }}
          >
            <div
              style={{
                height: `${(d.calls / peak) * 100}%`,
                minHeight: d.calls ? 2 : 0,
                background: 'var(--wr-signal)',
                borderRadius: 2,
                opacity: d.mcp ? 1 : 0.55,
              }}
            />
          </div>
        ))}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          ...muted,
          fontSize: 11,
          marginTop: 4,
        }}
      >
        <span>{dayLabel(days[0]?.day ?? u.period_start)}</span>
        <span>{dayLabel(days[days.length - 1]?.day ?? u.period_end)}</span>
      </div>

      <div
        style={{
          display: 'grid',
          gap: 20,
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          marginTop: 20,
        }}
      >
        <div>
          <div style={label}>By tool</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6 }}>
            <thead>
              <tr>
                <th style={th}>Tool</th>
                <th style={{ ...th, textAlign: 'right' }}>Calls</th>
                <th style={{ ...th, textAlign: 'right' }}>Errors</th>
                <th style={{ ...th, textAlign: 'right' }}>Avg ms</th>
              </tr>
            </thead>
            <tbody>
              {u.by_route.length === 0 && (
                <tr>
                  <td style={td} colSpan={4}>
                    <span style={muted}>No calls logged this month.</span>
                  </td>
                </tr>
              )}
              {u.by_route.map((r) => (
                <tr key={r.route}>
                  <td style={td}>{ROUTE_NAMES[r.route] ?? r.route}</td>
                  <td style={num}>{n(r.calls)}</td>
                  <td style={{ ...num, color: r.errors ? 'var(--wr-bad)' : undefined }}>
                    {n(r.errors)}
                  </td>
                  <td style={num}>{r.avg_ms ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <div style={label}>By API key</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6 }}>
            <thead>
              <tr>
                <th style={th}>Key</th>
                <th style={{ ...th, textAlign: 'right' }}>This month</th>
                <th style={{ ...th, textAlign: 'right' }}>Limit</th>
              </tr>
            </thead>
            <tbody>
              {u.keys.length === 0 && (
                <tr>
                  <td style={td} colSpan={3}>
                    <span style={muted}>No API keys yet.</span>
                  </td>
                </tr>
              )}
              {u.keys.map((k) => (
                <tr key={k.id} style={{ opacity: k.revoked ? 0.55 : 1 }}>
                  <td style={td}>
                    {k.name || 'Key'}{' '}
                    <span style={{ fontFamily: mono, fontSize: 12, color: 'var(--wr-muted)' }}>
                      {k.prefix}…
                    </span>
                    {k.revoked && <span style={{ ...muted, marginLeft: 6 }}>revoked</span>}
                    {!k.revoked && k.expires_at && (
                      <div style={{ ...muted, fontSize: 12 }}>
                        valid until {fullDay(k.expires_at)}
                      </div>
                    )}
                  </td>
                  <td style={num}>{n(k.month_calls)}</td>
                  <td style={num}>
                    {k.monthly_cap != null
                      ? n(k.monthly_cap)
                      : k.lifetime_cap != null
                        ? n(k.lifetime_cap)
                        : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p style={{ ...muted, fontSize: 12, margin: '16px 0 0' }}>
        The monthly count is what your allowance is measured against. The breakdown comes from the
        call log
        {u.logged_since
          ? `, which starts on ${dayLabel(u.logged_since)}`
          : ', which has no calls yet'}
        {logStartsLate
          ? `, so it explains ${n(u.logged_calls)} of the ${n(u.calls)} calls this month`
          : ''}
        . Brighter bars are days with MCP calls.
      </p>
    </>
  )
}

function Stat({ k, v, sub }: { k: string; v: string; sub: string }) {
  return (
    <div style={{ minWidth: 130 }}>
      <div style={label}>{k}</div>
      <div style={{ fontFamily: mono, fontSize: 28, fontWeight: 700, lineHeight: 1.15 }}>{v}</div>
      <div style={muted}>{sub}</div>
    </div>
  )
}
